/** @jsx jsx */
import { jsx, keyframes } from "@emotion/react";
import { Fragment } from "react";
import { useResizeDetector } from "react-resize-detector";
import { graphql } from "gatsby";
import App from "../components/App/";
import Head from "../components/Head";
import VisuallyHidden from "../components/VisuallyHidden";
import Card from "../components/Card";
import CardSquare from "../components/CardSquare";
import Canvas from "../components/Canvas";
import CanvasParams from "../types/CanvasParams";
import CanvasNorm from "../types/CanvasNorm";
import Footer from "../components/Footer";
import Nexo from "../../static/svg/nexo.svg";
import Helsinki from "../../static/svg/helsinki.svg";
import PropsIndex from "../types/PropsIndex";

const params: CanvasParams = {
  interval: 30,
  probSplit: 0.06,
  probTurn: 0.02,
  bufTop: 0,
  bufRight: 0,
  bufBottom: 0,
  bufLeft: 0,
};

const norms: CanvasNorm[] = [];

const initialPoints = [
  {
    pos: {
      x: 0,
      y: 0,
    },
    mov: {
      x: 2,
      y: 2,
    },
    ang: 54,
    angChange: 0,
    bif: 2,
    bifChange: 0,
    hue: 0,
    hueChange: 0,
    sat: 0,
    satChange: 0,
    lum: 100,
    lumChange: 0,
    opa: 100,
    opaChange: 0,
    rad: 5,
    radChange: 0,
  },
  {
    pos: {
      x: 0,
      y: 0,
    },
    mov: {
      x: 0.52,
      y: 1.93,
    },
    ang: 54,
    angChange: 0,
    bif: 3,
    bifChange: 0,
    hue: 0,
    hueChange: 0,
    sat: 0,
    satChange: 0,
    lum: 100,
    lumChange: 0,
    opa: 100,
    opaChange: 0,
    rad: 5,
    radChange: 0,
  },
];

const Index = (props: PropsIndex) => {
  const posts = props.data.allMarkdownRemark.edges;

  // follow the size of the viewport and the content
  const resizeDetector = useResizeDetector();

  const fadeIn = keyframes`from { opacity: 1; } to { opacity: 0; }`;

  return (
    <App>
      <Head />
      <header
        ref={resizeDetector.ref}
        css={{
          minHeight: "100vh",
          display: "relative",
          overflow: "hidden",
          position: "relative",
          backgroundColor: "black",
          background:
            "linear-gradient(180deg,hsl(0, 0%, 0%) 50%, hsl(0, 0%, 9.81%) 57%, hsl(0, 0%, 19.25%) 62.7%, hsl(0, 0%, 28.28%) 67.4%, hsl(0, 0%, 36.92%) 71.2%, hsl(0, 0%, 45.13%) 74.3%, hsl(0, 0%, 52.92%) 76.8%, hsl(0, 0%, 60.25%) 79%, hsl(0, 0%, 67.11%) 80.8%, hsl(0, 0%, 73.48%) 82.6%, hsl(0, 0%, 79.35%) 84.3%, hsl(0, 0%, 84.68%) 86.4%, hsl(0, 0%, 89.44%) 88.9%, hsl(0, 0%, 93.61%) 91.8%, hsl(0, 0%, 97.14%) 95.5%, hsl(0, 0%, 100%) 100%)",
        }}
      >
        <div
          css={{
            position: "absolute",
            top: "0",
            right: "0",
            bottom: "0",
            left: "0",
            zIndex: 300,
            backgroundColor: "black",
            opacity: "0",
            animationName: fadeIn,
            animationDelay: "0.5s",
            animationDuration: "1.5s",
            animationIterationCount: 1,
            animationTimingFunction: "ease",
            animationFillMode: "both",
          }}
        ></div>
        <div
          css={{
            position: "absolute",
            top: "0",
            right: "0",
            bottom: "0",
            left: "0",
            zIndex: 200,
            display: "flex",
            flexDirection: "column",
            /* stylelint-disable */
            justifyContent:
              !resizeDetector.width ||
              !resizeDetector.height ||
              resizeDetector.width < resizeDetector.height
                ? "center"
                : "flex-start",
            /* stylelint-enable */
            alignItems: "center",
          }}
        >
          <h1
            css={{
              fontSize: "9vw",
              color: "white",
            }}
          >
            <span
              css={{
                display: "inline-block",
                backgroundColor: "black",
                padding: "0.1em 0.4em",
                marginRight: "0.4em",
                borderColor: "white",
                borderWidth: "0.1em",
                borderStyle: "solid",
              }}
            >
              Roots
            </span>
            Issue 2/2021
          </h1>
          <div css={{ width: "82vw" }}>
            <Nexo
              css={{
                width: "100%",
                height: "100%",
                fill: "white",
              }}
            />
          </div>
          <VisuallyHidden>Nexo Creative Writing Group</VisuallyHidden>
        </div>
        <div>
          <Canvas
            width={resizeDetector.width ? resizeDetector.width : 1}
            height={resizeDetector.height ? resizeDetector.height : 1}
            params={params}
            initialPoints={initialPoints}
            norms={norms}
          />
        </div>
      </header>
      <main
        css={{
          marginTop: "3rem",
          marginRight: "3rem",
          marginLeft: "3rem",
          "@media (max-width: 800px)": {
            marginRight: "1.5rem",
            marginLeft: "1.5rem",
          },
        }}
      >
        <div
          css={{
            marginTop: "3rem",
            marginBottom: "3rem",
            paddingTop: "3rem",
            display: "grid",
            columnGap: "3rem",
            rowGap: "2rem",
            gridTemplateColumns: "1fr 1fr 1fr",
            "@media (max-width: 1200px)": {
              gridTemplateColumns: "1fr 1fr",
            },
            "@media (max-width: 800px)": {
              gridTemplateColumns: "1fr",
            },
          }}
        >
          <div
            css={{
              gridArea: "1 / 1 / 2 / 3",
              "@media (max-width: 1200px)": {
                gridArea: "1 / 1 / 2 / 2",
              },
              "@media (max-width: 800px)": {
                gridArea: "1 / 1 / 1 / 1",
              },
            }}
          >
            <p>
              <strong>
                Nexo is a writing group based in Helsinki, Finland.
              </strong>{" "}
              It brings together people who are beginning to search their
              identity as writers, or seek for a sense of community in their
              creative work. We believe that all writing is valuable, as it is a
              way of connecting with ourselves, each other and the world we
              inhabit.
            </p>
            <p>
              <strong>
                Nexo started in 2020, and continued with a new group of writers
                during February - April 2021.
              </strong>{" "}
              We met weekly to explore and practice different ways of connecting
              with our writing, understanding how our personal stories influence
              our writing and developing creativity in a shared,
              thought-provoking way. The group worked in three languages:
              Spanish, Finnish, and English.
            </p>
            <p>
              This publication is a product of the ideas and discussions that
              emerged during the spring. Nine writers collaborated with their
              own interpretation of the theme <strong>Roots</strong>.
            </p>
            <p>
              Nexo is organized by{" "}
              <a href="http://www.caisa.fi/">Caisa Cultural Center</a> and
              facilitated by Lois Armas (she/her) and Noora Pitkälä (she/her).
              The previous edition in 2020 was distributed in print and online.
              We invite you to check out{" "}
              <a href="https://issuu.com/nexocreativewriting/docs/issuuoriginalnexozineedit03122020">
                Issue 1/2020 - Mirrors
              </a>{" "}
              over at issuu.com.
            </p>
            <ul>
              <li>
                Email:{" "}
                <a href="mailto:nexocreativewriting@gmail.com">
                  nexocreativewriting@gmail.com
                </a>
              </li>
              <li>
                Facebook:{" "}
                <a href="https://www.facebook.com/nexocreativewriting">
                  Nexo Creative Writing
                </a>
              </li>
              <li>
                Instagram:{" "}
                <a href="https://www.instagram.com/nexocreativewriting/">
                  @nexocreativewriting
                </a>
              </li>
            </ul>
          </div>
          <Card data={posts[0].node.frontmatter} key={posts[0].node.id} />
        </div>
        <div
          css={{
            marginTop: "3rem",
            marginBottom: "3rem",
            display: "grid",
            columnGap: "3rem",
            rowGap: "2rem",
            gridTemplateColumns: "1fr 1fr 1fr",
            "@media (max-width: 1200px)": {
              gridTemplateColumns: "1fr 1fr",
            },
            "@media (max-width: 800px)": {
              gridTemplateColumns: "1fr",
            },
          }}
        >
          <Fragment>
            {posts.map((post, p) =>
              p > 0 ? (
                <CardSquare data={post.node.frontmatter} key={post.node.id} />
              ) : null
            )}
          </Fragment>
        </div>
      </main>
      <Footer />
      <div
        css={{
          height: "25vh",
          background:
            "linear-gradient(to bottom,hsl(0, 0%, 100%) 0%, hsl(0, 0%, 98.73%) 8.1%, hsl(0, 0%, 95.14%) 15.5%, hsl(0, 0%, 89.6%) 22.5%, hsl(0, 0%, 82.46%) 29%, hsl(0, 0%, 74.07%) 35.3%, hsl(0, 0%, 64.8%) 41.2%,hsl(0, 0%, 54.99%) 47.1%, hsl(0, 0%, 45.01%) 52.9%, hsl(0, 0%, 35.2%) 58.8%,hsl(0, 0%, 25.93%) 64.7%, hsl(0, 0%, 17.54%) 71%, hsl(0, 0%, 10.4%) 77.5%, hsl(0, 0%, 4.86%) 84.5%, hsl(0, 0%, 1.27%) 91.9%, hsl(0, 0%, 0%) 100%)",
        }}
      />
      <aside
        css={{
          backgroundColor: "black",
          height: "10rem",
          paddingBottom: "3rem",
          textAlign: "center",
        }}
      >
        <Helsinki
          css={{
            background: "black",
            height: "100%",
            fill: "white",
          }}
        />
        <VisuallyHidden>
          Supported by: Caisa Cultural Center and the City of Helsinki
        </VisuallyHidden>
      </aside>
    </App>
  );
};

const pageQuery = graphql`
  query {
    allMarkdownRemark(sort: { order: ASC, fields: frontmatter___order }) {
      edges {
        node {
          id
          frontmatter {
            title
            titleLang
            slug
            color
          }
        }
      }
    }
  }
`;

export default Index;
export { pageQuery };
